// Migrated 1:1 from @nexato/nx-core-module (file/components/file-list-item-v2, #686)
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, effect, inject, Inject, output, signal, ViewChild, computed, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { File } from '../../entities/file/file';
import { MenuItem } from 'primeng/api';
import { FilesService } from '../../services/file/file.service';
import { ConfirmDialogService } from '../confirmDialog/confirmDialogService';
import { ConfirmDialogEventType, ConfirmDialogSeverity } from '../confirmDialog/confirmDialogComponent';
import { HttpClient } from '@angular/common/http';
import { PdfJsViewerComponent } from 'ng2-pdfjs-viewer';

interface Environment {
  fileApi: string;
}

@Component({
  selector: 'app-file-list-item-v2',
  templateUrl: './file-list-item-v2.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'class': 'block border-b border-gray first:border-t'
  }
})
export class FileListItemV2Component {
  // Injected services
  private readonly fileService = inject(FilesService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly http = inject(HttpClient);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  private readonly environment: Environment;

  // Inputs & Outputs
  readonly file = input.required<File>();
  readonly ownerId = input<string>();
  readonly refetch = output<void>();

  // State
  readonly menuVisible = signal(false);
  readonly currentImage = signal<string>('');
  readonly currentHeader = signal<string>('');
  readonly imageDialogVisible = signal(false);
  readonly pdfDialogVisible = signal(false);
  private readonly pdfSrcData = signal<Blob | null>(null);

  @ViewChild('pdfViewerAutoLoad') pdfViewerAutoLoad: PdfJsViewerComponent;

  // Computed values
  readonly menuItems = computed(() => this.getMenuItems(this.file()));
  readonly shouldShowButton = computed(() => this.menuItems().length > 0);

  // Effect to update menu items when file changes
  constructor(@Inject('env') environment: Environment) {
    this.environment = environment;
    effect(() => {
      // Trigger recomputation when file changes
      this.file();
    });
  }

  getSizeinMb(size: number): string {
    return (size / (1024 * 1024)).toFixed(2);
  }

  private getMenuItems(file: File): MenuItem[] {
    return [
      {
        label: 'Herunterladen',
        icon: 'pi pi-fw pi-download',
        command: () => this.downloadFile(file)
      },
      {
        label: 'Löschen',
        icon: 'pi pi-fw pi-trash',
        command: () => this.confirmDialog(file)
      }
    ];
  }

  private confirmDialog(file: File): void {
    const dialogRef = this.confirmDialogService.open({
      message: 'Möchtest Du die Datei wirklich löschen?',
      severity: ConfirmDialogSeverity.WARN,
      header: 'Datei löschen'
    });

    dialogRef.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: ConfirmDialogEventType) => {
        if (result === ConfirmDialogEventType.CONFIRM) {
          this.fileService.deleteFile(file, () => {
            this.refetch.emit();
          });
        }
      });
  }

  onMenuShow(): void {
    this.menuVisible.set(true);
  }

  onMenuHide(): void {
    this.menuVisible.set(false);
  }

  downloadFile(file: File): void {
    this.fileService.downloadFile(file);
  }

  openFile(file: File): void {
    // open application/pdf, image/jpg, image/jpeg, image/png
    if (file?.extension === 'jpg' || file?.extension === 'jpeg' || file.extension === 'png') {
      this.currentImage.set(this.environment.fileApi + '/view/' + file?.fullPath);
      this.currentHeader.set(file?.fileName);
      this.imageDialogVisible.set(true);
      // t5x is a quick fix, because the backend does not store it correctly
    } else if (file?.extension === 'pdf' || file?.extension === 't5x') {
      const url = this.environment.fileApi + '/file/' + file?.fullPath;
      this.http.get(url, { responseType: 'blob' })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response: Blob) => {
          this.currentHeader.set(file?.fileName);
          this.pdfSrcData.set(response);
          this.pdfDialogVisible.set(true);
          this.changeDetectorRef.detectChanges();

          // Use Promise to ensure proper timing
          Promise.resolve().then(() => {
            if (this.pdfViewerAutoLoad) {
              this.initializePdfViewer(file?.fileName);
            }
          });
        });
    }
  }

  private initializePdfViewer(fileName: string): void {
    const pdfData = this.pdfSrcData();
    if (!this.pdfViewerAutoLoad || !pdfData) return;

    this.pdfViewerAutoLoad.diagnosticLogs = true;
    this.pdfViewerAutoLoad.pdfSrc = pdfData;
    this.pdfViewerAutoLoad.downloadFileName = fileName;
    this.pdfViewerAutoLoad.openFile = false;

    // Ensure change detection runs after initialization
    this.changeDetectorRef.detectChanges();
    this.pdfViewerAutoLoad.refresh();
  }
}
